/**
 * KittyBOT Homepage Hero Slider.
 * Lightweight native Web Component. No third-party libraries.
 */
(() => {
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  class KbHeroSlider extends HTMLElement {
    constructor() {
      super();
      this._index = 0;
      this._timer = null;
      this._hovering = false;
      this._focused = false;
      this._manualCooldownUntil = 0;
      this._initialized = false;
    }

    connectedCallback() {
      if (this._initialized) return;

      this.track = this.querySelector('[data-hero-track]');
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      if (!this.track || this.slides.length === 0) {
        // Children may not be parsed yet when the definition upgrades the element.
        if (!this._retryCount) this._retryCount = 0;
        if (this._retryCount < 50) {
          this._retryCount += 1;
          window.requestAnimationFrame(() => this.connectedCallback());
        }
        return;
      }
      this._initialized = true;

      this.slideCount = this.slides.length;
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplaySpeed = (parseInt(this.dataset.autoplaySpeed || '6', 10) || 6) * 1000;
      this.showArrows = this.dataset.showArrows !== 'false';
      this.showDots = this.dataset.showDots !== 'false';
      this.pauseOnHover = this.dataset.pauseOnHover !== 'false';

      this._buildControls();
      this._bindEvents();
      this._boundResize = () => this._syncHeight();
      window.addEventListener('resize', this._boundResize);
      this._goTo(0, false);

      if (this.slideCount > 1) {
        this._bindEditorEvents();
        if (this.autoplayEnabled && !reducedMotionQuery.matches) {
          this._startAutoplay();
        }
      }
    }

    disconnectedCallback() {
      this._stopAutoplay();
      if (this._boundResize) {
        window.removeEventListener('resize', this._boundResize);
        this._boundResize = null;
      }
      if (this._boundHandlers) {
        const { onKey, onMouseEnter, onMouseLeave, onFocusIn, onFocusOut, onVisibility, onTouchStart, onTouchEnd, onSectionUnload, onBlockSelect } = this._boundHandlers;
        this.removeEventListener('keydown', onKey);
        this.removeEventListener('mouseenter', onMouseEnter);
        this.removeEventListener('mouseleave', onMouseLeave);
        this.removeEventListener('focusin', onFocusIn);
        this.removeEventListener('focusout', onFocusOut);
        this.removeEventListener('touchstart', onTouchStart, { passive: true });
        this.removeEventListener('touchend', onTouchEnd);
        document.removeEventListener('visibilitychange', onVisibility);
        document.removeEventListener('shopify:section:unload', onSectionUnload);
        document.removeEventListener('shopify:block:select', onBlockSelect);
        this._boundHandlers = null;
      }
    }

    _buildControls() {
      this.controls = this.querySelector('[data-hero-controls]');
      if (!this.controls || this.slideCount <= 1) return;

      this.prevBtn = this.querySelector('[data-hero-prev]');
      this.nextBtn = this.querySelector('[data-hero-next]');
      this.dotsWrap = this.querySelector('[data-hero-dots]');

      if (this.dotsWrap) {
        this.dots = [];
        for (let i = 0; i < this.slideCount; i += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'kb-hero-slider__dot';
          dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
          dot.addEventListener('click', () => this._goTo(i, true));
          this.dotsWrap.appendChild(dot);
          this.dots.push(dot);
        }
      }

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this._goTo(this._index - 1, true);
          this._markManualInteraction();
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this._goTo(this._index + 1, true);
          this._markManualInteraction();
        });
      }
      if (!this.showArrows) {
        if (this.prevBtn) this.prevBtn.hidden = true;
        if (this.nextBtn) this.nextBtn.hidden = true;
      }
      if (!this.showDots && this.dotsWrap) this.dotsWrap.hidden = true;
    }

    _bindEvents() {
      const onKey = (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this._goTo(this._index - 1, true);
          this._markManualInteraction();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this._goTo(this._index + 1, true);
          this._markManualInteraction();
        }
      };
      const onMouseEnter = () => {
        this._hovering = true;
        if (this.pauseOnHover) this._pauseAutoplay();
      };
      const onMouseLeave = () => { this._hovering = false; this._maybeResume(); };
      const onFocusIn = () => { this._focused = true; this._pauseAutoplay(); };
      const onFocusOut = () => { this._focused = false; this._maybeResume(); };
      const onVisibility = () => { if (document.hidden) this._pauseAutoplay(); else this._maybeResume(); };
      let touchStartX = null;
      const onTouchStart = (event) => {
        const touch = event.changedTouches && event.changedTouches[0];
        touchStartX = touch ? touch.clientX : null;
      };
      const onTouchEnd = (event) => {
        if (touchStartX === null) return;
        const touch = event.changedTouches && event.changedTouches[0];
        if (!touch) return;
        const delta = touch.clientX - touchStartX;
        if (Math.abs(delta) > 50) {
          this._goTo(delta < 0 ? this._index + 1 : this._index - 1, true);
          this._markManualInteraction();
        }
        touchStartX = null;
      };
      const onSectionUnload = (event) => {
        if (event.detail && this.contains(document.querySelector(`[data-block-id="${event.detail.blockId}"]`))) {
          this._stopAutoplay();
        }
      };
      const onBlockSelect = (event) => {
        const blockId = event.detail && event.detail.blockId;
        if (!blockId) return;
        const slide = this.querySelector(`[data-hero-slide][data-block-id="${blockId}"]`);
        if (slide) {
          const index = this.slides.indexOf(slide);
          if (index >= 0) this._goTo(index, false);
        }
      };

      this._boundHandlers = { onKey, onMouseEnter, onMouseLeave, onFocusIn, onFocusOut, onVisibility, onTouchStart, onTouchEnd, onSectionUnload, onBlockSelect };

      this.addEventListener('keydown', onKey);
      this.addEventListener('mouseenter', onMouseEnter);
      this.addEventListener('mouseleave', onMouseLeave);
      this.addEventListener('focusin', onFocusIn);
      this.addEventListener('focusout', onFocusOut);
      this.addEventListener('touchstart', onTouchStart, { passive: true });
      this.addEventListener('touchend', onTouchEnd);
      document.addEventListener('visibilitychange', onVisibility);
      document.addEventListener('shopify:section:unload', onSectionUnload);
      document.addEventListener('shopify:block:select', onBlockSelect);
    }

    _bindEditorEvents() {
      // Re-init guard: if Shopify re-renders the section, the element is replaced and
      // connectedCallback runs again on the fresh node. Nothing else required here.
    }

    _syncHeight() {
      const activeSlide = this.slides && this.slides[this._index];
      if (!activeSlide || !this.track) return;
      this.track.style.height = `${activeSlide.offsetHeight}px`;
    }

    _goTo(index, userInitiated) {
      if (this.slideCount === 0) return;
      this._index = (index + this.slideCount) % this.slideCount;

      if (this.track) {
        this.track.style.transform = `translateX(-${this._index * 100}%)`;
      }

      this.slides.forEach((slide, i) => {
        const active = i === this._index;
        slide.dataset.active = String(active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (active) {
          slide.removeAttribute('inert');
        } else {
          slide.setAttribute('inert', '');
        }
        if (active) {
          slide.setAttribute('aria-current', 'true');
        } else {
          slide.removeAttribute('aria-current');
        }
        slide.querySelectorAll('a, button').forEach((el) => {
          if (active) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', '-1');
        });
      });

      if (this.dots) {
        this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this._index));
      }

      this._syncHeight();
    }


    _startAutoplay() {
      this._stopAutoplay();
      if (!this.autoplayEnabled || reducedMotionQuery.matches || this.slideCount <= 1) return;
      this._timer = window.setInterval(() => {
        if (this._hovering || this._focused || document.hidden) return;
        if (Date.now() < this._manualCooldownUntil) return;
        this._goTo(this._index + 1, false);
      }, this.autoplaySpeed);
    }

    _stopAutoplay() {
      if (this._timer) {
        window.clearInterval(this._timer);
        this._timer = null;
      }
    }

    _pauseAutoplay() {
      this._stopAutoplay();
    }

    _resumeAutoplay() {
      this._startAutoplay();
    }

    _maybeResume() {
      if (!this._hovering && !this._focused && !document.hidden) {
        this._startAutoplay();
      }
    }

    _markManualInteraction() {
      const cooldown = Math.max(10000, this.autoplaySpeed);
      this._manualCooldownUntil = Date.now() + cooldown;
      this._pauseAutoplay();
      window.setTimeout(() => {
        if (!this._hovering && !this._focused && !document.hidden && this.autoplayEnabled) {
          this._resumeAutoplay();
        }
      }, cooldown);
    }

  }

  if (!customElements.get('kb-hero-slider')) {
    customElements.define('kb-hero-slider', KbHeroSlider);
  }
})();
